/* cc-card-product — behavior + client renderer.
   - Click delegation for [data-cc-atc]: single-variant products add
     straight to cart; multi-variant products open cc-variant-modal.
   - window.ccAddToCart(variantId): adds + refreshes Dawn's cart
     drawer/icon via the Section Rendering API.
   - window.ccRenderProductCard(p): builds card HTML from
     /collections/<handle>/products.json data (client twin of
     snippets/cc-card-product.liquid — keep markup in sync). */

(() => {
  /* init guard — more than one section can load this file on a page;
     duplicate <script src> tags each execute (double delegation = double ATC) */
  if (window.ccInitGuardccCardProduct) return;
  window.ccInitGuardccCardProduct = true;

  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

  window.ccAddToCart = async function (variantId, button) {
    if (button) {
      button.disabled = true;
      button.classList.add('cc-btn--loading'); // spinner (cc-tokens.css)
      button.setAttribute('aria-busy', 'true');
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, sections: 'cart-drawer,cart-icon-bubble' }),
      });
      if (!res.ok) throw new Error('add failed');
      const data = await res.json();

      const bubble = document.getElementById('cart-icon-bubble');
      if (data.sections && data.sections['cart-icon-bubble'] && bubble) {
        bubble.innerHTML = new DOMParser()
          .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section').innerHTML;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents({ sections: data.sections });
      }

      if (button) {
        const label = button.querySelector('svg') ? null : button.textContent;
        button.dataset.state = 'added';
        if (label) button.textContent = 'Added ✓';
        setTimeout(() => {
          delete button.dataset.state;
          if (label) button.textContent = label;
        }, 1600);
      }
      return true;
    } catch {
      // graceful fallback: send them to the cart page
      window.location.href = '/cart';
      return false;
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove('cc-btn--loading');
        button.removeAttribute('aria-busy');
      }
    }
  };

  document.addEventListener('click', (e) => {
    /* sold-out cards: our single notify button delegates to Stoq's
       (CSS-hidden) injected collection button — programmatic .click()
       still fires its handler. Cards injected after load (category
       rail, recently viewed, related-more tail) never get the
       injection: fall back to the PDP, where the sticky bar owns the
       notify flow. */
    const nbtn = e.target.closest('[data-cc-notify]');
    if (nbtn) {
      const ncard = nbtn.closest('.cc-cprod');
      if (!ncard) return;
      e.preventDefault();
      const stoq = ncard.querySelector('.restock-rocket-button-collection');
      if (stoq) {
        stoq.click();
      } else {
        window.location.href = '/products/' + ncard.dataset.handle;
      }
      return;
    }
    const btn = e.target.closest('[data-cc-atc]');
    if (!btn) return;
    const card = btn.closest('.cc-cprod');
    if (!card) return;
    e.preventDefault();
    const variantId = card.dataset.variantId;
    if (variantId) {
      window.ccAddToCart(variantId, btn);
    } else if (window.ccVariantModal) {
      window.ccVariantModal.open(card.dataset.handle);
    } else {
      window.location.href = '/products/' + card.dataset.handle;
    }
  });

  /* Client renderer — from products.json shape */
  window.ccRenderProductCard = function (p) {
    const v0 = p.variants && p.variants[0];
    const availVariants = (p.variants || []).filter((v) => v.available);
    const avail = availVariants.length > 0;
    /* direct-add: single variant, or exactly one variant left available
       (Tony 2026-07-11) — card shows/adds THAT variant incl. its price */
    const multi = p.variants && p.variants.length > 1;
    const direct = !multi ? v0 : availVariants.length === 1 ? availVariants[0] : null;
    /* price mirrors the server card (Tony 2026-07-14): no direct-add →
       show the product MIN price with a "from" prefix when variants
       differ (server uses product.price = min; the old twin showed the
       FIRST variant's price, which could overstate). */
    const priced = (p.variants || [])
      .map((v) => ({ v, n: parseFloat(v.price) }))
      .filter((x) => !isNaN(x.n));
    const minEntry = priced.length ? priced.reduce((a, b) => (b.n < a.n ? b : a)) : null;
    const maxN = priced.length ? priced.reduce((a, b) => (b.n > a.n ? b : a)).n : 0;
    const priceSrc = direct || (minEntry && minEntry.v) || v0;
    const price = priceSrc ? parseFloat(priceSrc.price) : 0;
    const was = priceSrc && priceSrc.compare_at_price ? parseFloat(priceSrc.compare_at_price) : 0;
    const onSale = was > price;
    const priceFrom = !direct && minEntry && maxN > minEntry.n;
    const label = !avail ? 'Notify me when available' : direct ? 'Add to cart' : 'Choose options';
    /* direct-add variant's image wins over the product image (Tony 2026-07-11) */
    const imgSrcRaw =
      (direct && direct.featured_image && direct.featured_image.src) ||
      (p.images && p.images[0] && p.images[0].src) ||
      null;
    const img = imgSrcRaw ? imgSrcRaw + (imgSrcRaw.includes('?') ? '&' : '?') + 'width=500' : null;
    const money = (n) => '$' + n.toFixed(2);
    /* Judge.me stars — only when the caller supplies p.rating = {v, c}
       (metafields aren't in products.json). Markup mirrors the snippet;
       an empty rating div keeps its reserved height (grid alignment). */
    const rating = p.rating && p.rating.c > 0
      ? `<span class="cc-cprod__stars" role="img" aria-label="Rated ${p.rating.v} out of 5 stars"><span class="cc-cprod__stars-fill" style="width: ${Math.round((p.rating.v / 5) * 1000) / 10}%" aria-hidden="true">★★★★★</span><span class="cc-cprod__stars-base" aria-hidden="true">★★★★★</span></span><span class="cc-cprod__stars-count">(${p.rating.c})</span>`
      : '';

    return `<div class="cc-cprod${avail ? '' : ' cc-cprod--soldout'}" data-handle="${esc(p.handle)}"${direct ? ` data-variant-id="${direct.id}"` : ''}>
      ${onSale ? '<span class="cc-cprod__badge">Sale</span>' : ''}
      ${avail ? `<button type="button" class="cc-cprod__fab" data-cc-atc aria-label="${label}">
        <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
      </button>` : ''}
      <a class="cc-cprod__media" href="/products/${esc(p.handle)}" tabindex="-1" aria-hidden="true">
        ${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" width="500" height="500" loading="lazy">` : ''}
      </a>
      <div class="cc-cprod__info">
        <div class="cc-cprod__rating">${rating}</div>
        <a class="cc-cprod__title" href="/products/${esc(p.handle)}">${esc(p.title)}</a>
        ${p.vendor ? `<p class="cc-cprod__vendor">${esc(p.vendor)}</p>` : ''}
        <p class="cc-cprod__price">${priceFrom ? '<span class="cc-cprod__price-from">from</span> ' : ''}${
          onSale
            ? `<span class="cc-cprod__price--was">${money(was)}</span><span class="cc-cprod__price--sale">${money(price)}</span>`
            : money(price)
        }</p>
        <p class="cc-cprod__stock cc-cprod__stock--${avail ? 'in' : 'out'}">${avail ? 'In stock' : 'Out of stock'}</p>
      </div>
      <div class="cc-cprod__actions">
        <div class="cc-cprod__actions-inner">
          <button type="button" class="cc-cprod__atc" ${avail ? 'data-cc-atc' : 'data-cc-notify'}>${label}</button>
        </div>
      </div>
    </div>`;
  };
})();
